<template>
  <div class="checkin-page">
    <div class="checkin-card" :class="{ 'animate-in': pageLoaded }">
      <div class="wave-decoration">
        <svg viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path
            fill="rgba(255,255,255,0.3)"
            d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
        </svg>
      </div>

      <div class="title-section">
        <div class="title-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 class="main-title" v-if="classroomName">
          {{ classroomName }}
        </h1>
        <h1 class="main-title" v-else>每日签到</h1>
        <p class="sub-title">记录每一个美好的开始</p>
      </div>

      <div class="circle-btn-section">
        <button
          class="circle-checkin-btn"
          :class="{ 'btn-pulse': !loading, 'btn-loading': loading }"
          @click="handleCheckIn"
          :disabled="loading"
        >
          <div class="btn-glow"></div>
          <div class="circle-inner" v-if="!loading">
            <svg
              class="circle-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
            <span class="circle-text" id="hhhh">签到</span>
          </div>
          <div class="loading-spinner" v-else></div>
        </button>
      </div>

      <div class="current-time-section">
        <div class="time-card">
          <p class="current-time">{{ currentTime }}</p>
          <p class="current-date">{{ currentDate }}</p>
        </div>
      </div>

      <div class="location-section" v-if="locationAddress || locationLoading">
        <div class="location-card">
          <div class="location-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <div class="location-info">
            <p class="location-label">
              当前位置<span v-if="latitude && longitude" class="location-coords"
                >（{{ latitude }}, {{ longitude }}）</span
              >
            </p>
            <div v-if="!locationLoading" class="location-content">
              <p class="location-address">{{ locationAddress }}</p>
              <button
                v-if="
                  locationAddress &&
                  (locationAddress.includes('失败') ||
                    locationAddress.includes('拒绝') ||
                    locationAddress.includes('超时') ||
                    locationAddress.includes('不支持'))
                "
                class="retry-btn"
                @click="getLocation"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M4 20v-6h6" />
                  <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
                <span>重试</span>
              </button>
            </div>
            <p class="location-loading" v-else>
              <span class="loading-dots">正在获取位置</span>
            </p>
          </div>
        </div>
      </div>

      <div class="footer-info">
        <p>大秦教育 · 签到系统</p>
      </div>
    </div>

    <transition name="modal">
      <div v-if="showSuccess" class="modal-overlay" @click.self="closeModal">
        <div class="success-modal">
          <div class="success-animation">
            <div class="success-icon-wrapper">
              <svg
                class="success-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div class="confetti">
              <span v-for="n in 12" :key="n" :class="`confetti-${n}`"></span>
            </div>
          </div>
          <h2 class="success-title">签到成功</h2>
          <p class="success-subtitle">今天也是充满活力的一天！</p>
          <div class="success-details">
            <div class="detail-card">
              <div class="detail-icon time-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">签到时间</span>
                <span class="detail-value">{{ checkInTime }}</span>
              </div>
            </div>
            <div class="detail-card">
              <div class="detail-icon location-icon-small">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div class="detail-content">
                <span class="detail-label">签到地点</span>
                <span class="detail-value">{{
                  locationAddress || "未知位置"
                }}</span>
              </div>
            </div>
          </div>
          <button class="confirm-btn" @click="closeModal">确定</button>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div
        v-if="showError"
        class="modal-overlay"
        @click.self="showError = false"
      >
        <div class="success-modal error-modal">
          <div class="success-icon-wrapper error-icon-wrapper">
            <svg
              class="success-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 class="success-title error-title">签到失败</h2>
          <p class="error-message">{{ errorMsg }}</p>
          <button class="confirm-btn error-btn" @click="showError = false">
            确定
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import {
  qiandao,
  weixinlog,
  getWechatJsConfig,
  getAddress,
  getclassroom,
} from "@/api/api";
import { useRoute } from "vue-router";
export default {
  name: "CheckInPage",
  setup() {
    const showSuccess = ref(false);
    const showError = ref(false);
    const checkInTime = ref("");
    const pageLoaded = ref(false);
    const currentTime = ref("");
    const currentDate = ref("");
    const loading = ref(false);
    const errorMsg = ref("");
    const locationAddress = ref("");
    const locationLoading = ref(false);
    const classroomName = ref("");
    const latitude = ref(0);
    const longitude = ref(0);
    const route = useRoute();
    let timeInterval = null;

    // 兼容获取classroomId：Hash路由下query参数可能在#前面也可能在#后面
    const getClassroomId = () => {
      let classroomId = route.query.classroomid;
      if (classroomId) return classroomId;
      try {
        // 从 hash 中的 query 参数获取（如 #/?classroomid=3）
        const hash = window.location.hash;
        const searchIndex = hash.indexOf("?");
        if (searchIndex !== -1) {
          const searchParams = new URLSearchParams(hash.substring(searchIndex));
          classroomId = searchParams.get("classroomid");
          if (classroomId) return classroomId;
        }
        // 从 # 前面的 query 参数获取（如 ?classroomid=3#/)
        const urlParams = new URLSearchParams(window.location.search);
        classroomId = urlParams.get("classroomid");
      } catch (e) {
        console.warn("解析classroomid失败:", e);
      }
      return classroomId || "";
    };

    const updateTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      currentTime.value = `${hours}:${minutes}:${seconds}`;

      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const weekDays = [
        "星期日",
        "星期一",
        "星期二",
        "星期三",
        "星期四",
        "星期五",
        "星期六",
      ];
      const weekDay = weekDays[now.getDay()];
      currentDate.value = `${year}年${month}月${day}日 ${weekDay}`;
    };

    const loadWechatSdk = () => {
      return new Promise((resolve, reject) => {
        if (window.wx) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("加载微信JS-SDK失败"));
        document.head.appendChild(script);
      });
    };

    const initWechatConfig = async () => {
      // eslint-disable-next-line no-useless-catch
      try {
        const url = window.location.href.split("#")[0];
        const response = await getWechatJsConfig(url);
        const data = response.data;

        if (data.code === 1) {
          window.wx.config({
            debug: false,
            appId: data.data.appId,
            timestamp: data.data.timestamp,
            nonceStr: data.data.nonceStr,
            signature: data.data.signature,
            jsApiList: ["getLocation"],
          });
        }

        return new Promise((resolve, reject) => {
          window.wx.ready(() => resolve());
          window.wx.error((err) => reject(err));
        });
      } catch (error) {
        throw error;
      }
    };

    const getLocation = async () => {
      if (!navigator.userAgent.includes("MicroMessenger")) {
        getLocationFallback();
        return;
      }

      locationLoading.value = true;

      try {
        await loadWechatSdk();
        await initWechatConfig();
        window.wx.getLocation({
          type: "gcj02", // 让微信帮你转到火星坐标系
          isHighAccuracy: true, // 启用高精度（建议开）虽然开了高精度但是不一定真的能拿到
          highAccuracyExpireTime: 5000, //最多等5秒不死等
          success: async (res) => {
            latitude.value = res.latitude;
            longitude.value = res.longitude;
            if (res.accuracy && res.accuracy > 100) {
              console.warn("定位精度较低:", res.accuracy + "米");
            }
            await reverseGeocode(res.latitude, res.longitude, "gcj02");
          },
          fail: (err) => {
            console.error("微信定位失败:", err);
            getLocationFallback();
          },
        });
      } catch (error) {
        console.warn("使用微信定位失败，尝试浏览器定位:", error);
        getLocationFallback();
      }
    };

    const getLocationFallback = () => {
      if (!navigator.geolocation) {
        locationAddress.value = "您的浏览器不支持定位,请升级浏览器";
        locationLoading.value = false;
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          //console.log("浏览器定位成功:", position);
          latitude.value = position.coords.latitude;
          longitude.value = position.coords.longitude;
          await reverseGeocode(
            position.coords.latitude,
            position.coords.longitude,
            "wgs84"
          );
        },
        (error) => {
          console.error("定位失败:", error);
          locationLoading.value = false;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              locationAddress.value = "定位权限已拒绝，请在浏览器设置中开启";
              break;
            case error.POSITION_UNAVAILABLE:
              locationAddress.value = "无法获取位置信息";
              break;
            case error.TIMEOUT:
              locationAddress.value = "定位超时，请稍后重试";
              break;
            default:
              locationAddress.value = `定位失败: ${error.message}`;
          }
        },
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 300000,
        }
      );
    };

    const bd09ToGcj02 = (bd_lat, bd_lng) => {
      const x_pi = (3.14159265358979324 * 3000.0) / 180.0;
      const x = bd_lng - 0.0065;
      const y = bd_lat - 0.006;
      const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * x_pi);
      const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * x_pi);
      return {
        lat: z * Math.sin(theta),
        lng: z * Math.cos(theta),
      };
    };

    const wgs84ToGcj02 = (wgs_lat, wgs_lng) => {
      const a = 6378137.0;
      const ee = 0.00669342162296594323;
      const dLat = transformLat(wgs_lng - 105.0, wgs_lat - 35.0);
      const dLng = transformLng(wgs_lng - 105.0, wgs_lat - 35.0);
      const radLat = (wgs_lat / 180.0) * Math.PI;
      let magic = Math.sin(radLat);
      magic = 1 - ee * magic * magic;
      const sqrtMagic = Math.sqrt(magic);
      const dLat1 =
        (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * Math.PI);
      const dLng1 =
        (dLng * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * Math.PI);
      return {
        lat: wgs_lat + dLat1,
        lng: wgs_lng + dLng1,
      };
    };

    const transformLat = (x, y) => {
      let ret =
        -100.0 +
        2.0 * x +
        3.0 * y +
        0.2 * y * y +
        0.1 * x * y +
        0.2 * Math.sqrt(Math.abs(x));
      ret +=
        ((20.0 * Math.sin(6.0 * x * Math.PI) +
          20.0 * Math.sin(2.0 * x * Math.PI)) *
          2.0) /
        3.0;
      ret +=
        ((20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin((y / 3.0) * Math.PI)) *
          2.0) /
        3.0;
      ret +=
        ((160.0 * Math.sin((y / 12.0) * Math.PI) +
          320 * Math.sin((y * Math.PI) / 30.0)) *
          2.0) /
        3.0;
      return ret;
    };

    const transformLng = (x, y) => {
      let ret =
        300.0 +
        x +
        2.0 * y +
        0.1 * x * x +
        0.1 * x * y +
        0.1 * Math.sqrt(Math.abs(x));
      ret +=
        ((20.0 * Math.sin(6.0 * x * Math.PI) +
          20.0 * Math.sin(2.0 * x * Math.PI)) *
          2.0) /
        3.0;
      ret +=
        ((20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin((x / 3.0) * Math.PI)) *
          2.0) /
        3.0;
      ret +=
        ((150.0 * Math.sin((x / 12.0) * Math.PI) +
          300.0 * Math.sin((x / 30.0) * Math.PI)) *
          2.0) /
        3.0;
      return ret;
    };

    const reverseGeocode = async (lat, lng, coordType = "gcj02") => {
      try {
        let targetLat = lat;
        let targetLng = lng;
        if (coordType === "wgs84") {
          const gcj = wgs84ToGcj02(lat, lng);
          targetLat = gcj.lat;
          targetLng = gcj.lng;
        } else if (coordType === "bd09") {
          const gcj = bd09ToGcj02(lat, lng);
          targetLat = gcj.lat;
          targetLng = gcj.lng;
        }
        //获取当前签到教室的信息
        const response = await getAddress(`${targetLat},${targetLng}`);
        const data = response.data;
        if (data.code === 1 && data.data.status === 0) {
          // 使用腾讯地图API返回的标准化GCJ-02坐标，避免微信定位可能返回WGS-84的问题
          if (data.data.result.location) {
            latitude.value = data.data.result.location.lat;
            longitude.value = data.data.result.location.lng;
          }
          const addressComponent =
            data.data.result.formatted_addresses.recommend;
          const addrs = data.data.result.address;
          locationAddress.value = addrs + addressComponent;
        } else {
          // API调用失败时回退到本地转换的坐标
          latitude.value = targetLat;
          longitude.value = targetLng;
          locationAddress.value = data.data.message;
        }
      } catch (error) {
        latitude.value = lat;
        longitude.value = lng;
        locationAddress.value = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      } finally {
        locationLoading.value = false;
      }
    };

    const getClassRoom = async (classroomid) => {
      try {
        const classroominfo = await getclassroom(classroomid);
        const res = classroominfo.data;
        if (res.code === 1) {
          classroomName.value = res.data.name;
        }
      } catch (error) {
        console.error("请求失败:", error);
      }
    };
    onMounted(() => {
      setTimeout(() => {
        pageLoaded.value = true;
      }, 100);
      //获取签到教室信息
      const classroomId = getClassroomId();
      if (classroomId) {
        getClassRoom(classroomId);
      }
      updateTime();
      timeInterval = setInterval(updateTime, 1000);
      getLocation();
    });

    onUnmounted(() => {
      if (timeInterval) {
        clearInterval(timeInterval);
      }
    });

    const isLoggedIn = () => {
      const token = localStorage.getItem("token");
      return token && token.length > 0;
    };

    const handleCheckIn = async () => {
      if (loading.value) {
        return;
      }

      if (!isLoggedIn()) {
        loading.value = true;
        try {
          const classroomid = getClassroomId();
          const response = await weixinlog({
            classroomid: classroomid,
            qdredirecturl:
              "https://qd.q.daqinjiaoyu.cn/#/weixin/callback?accesstoken=",
          });
          const resData = response.data;
          if (resData.code === 1 && resData.data.redirectUrl) {
            window.location.replace(resData.data.redirectUrl);
          } else {
            throw new Error(resData?.message || "获取授权链接失败");
          }
        } catch (error) {
          console.error("登录失败:", error);
          errorMsg.value = error.message || "登录失败，请稍后重试";
          showError.value = true;
          loading.value = false;
        }
        return;
      }

      if (latitude.value === 0 || longitude.value === 0) {
        errorMsg.value = "请先获取位置信息";
        showError.value = true;
        return;
      }

      loading.value = true;

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const classroomId = getClassroomId();
      checkInTime.value = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      try {
        const checkInData = {
          checkInTime: checkInTime.value,
          timestamp: now.getTime(),
          location: locationAddress.value || "",
          lat: latitude.value,
          lng: longitude.value,
          classroomid: classroomId,
        };

        console.log("发送签到请求:", checkInData);
        const response = await qiandao(checkInData);
        const resData = response.data;
        //如果返回了401代表登录过期了从新登录
        if (resData.code === 401) {
          const classroomid = getClassroomId();
          const response = await weixinlog({
            classroomid: classroomid,
            qdredirecturl:
              "https://qd.q.daqinjiaoyu.cn/#/weixin/callback?accesstoken=",
          });
          const resData = response.data;
          if (resData.code === 1 && resData.data.redirectUrl) {
            window.location.replace(resData.data.redirectUrl);
          } else {
            throw new Error(resData?.info || "获取授权链接失败");
          }
        }
        if (resData.code === 1) {
          showSuccess.value = true;
        } else {
          throw new Error(resData?.info || "签到失败");
        }
      } catch (error) {
        console.error("签到失败:", error);
        errorMsg.value =
          error.message ||
          error.response?.data?.message ||
          "网络请求失败，请稍后重试";
        showError.value = true;
      } finally {
        loading.value = false;
      }
    };

    const closeModal = () => {
      showSuccess.value = false;
    };

    return {
      showSuccess,
      showError,
      checkInTime,
      pageLoaded,
      currentTime,
      currentDate,
      loading,
      errorMsg,
      locationAddress,
      locationLoading,
      classroomName,
      handleCheckIn,
      closeModal,
    };
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;600;700&family=Noto+Sans+SC:wght@300;400;500;700&display=swap");

:root {
  --primary-blue: #4a90e2;
  --primary-light: #6ba3e8;
  --primary-dark: #3a7bc8;
  --accent-cyan: #5ac8fa;
  --bg-gradient-start: #e8f4fd;
  --bg-gradient-end: #d6ebfa;
  --card-bg: rgba(255, 255, 255, 0.95);
  --text-primary: #2c3e50;
  --text-secondary: #5a6c7d;
  --text-muted: #8fa1b3;
  --shadow-soft: 0 10px 40px rgba(74, 144, 226, 0.15);
  --shadow-medium: 0 20px 60px rgba(74, 144, 226, 0.2);
}

.checkin-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #e8f4fd 0%, #d6ebfa 50%, #c5e3f8 100%);
  position: relative;
  overflow: hidden;
}

.checkin-page::before,
.checkin-page::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.checkin-page::before {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -200px;
  animation: float 20s ease-in-out infinite;
}

.checkin-page::after {
  width: 400px;
  height: 400px;
  bottom: -100px;
  left: -100px;
  animation: float 25s ease-in-out infinite reverse;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.checkin-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(74, 144, 226, 0.15),
    0 4px 20px rgba(74, 144, 226, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  padding: 40px 32px;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.checkin-card.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.wave-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 120px;
  overflow: hidden;
  pointer-events: none;
}

.wave-decoration svg {
  width: 100%;
  height: 100%;
}

.title-section {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.title-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(74, 144, 226, 0.4);
  }
}

.title-icon svg {
  width: 32px;
  height: 32px;
  color: white;
}

.main-title {
  font-family: "Noto Serif SC", serif;
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
  letter-spacing: 4px;
}

.sub-title {
  font-size: 14px;
  color: #5ac8fa;
  font-weight: 400;
  letter-spacing: 2px;
}

.circle-btn-section {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  position: relative;
}

.circle-checkin-btn {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  box-shadow: 0 12px 40px rgba(74, 144, 226, 0.35);
}

.btn-glow {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  opacity: 0.3;
  filter: blur(8px);
  z-index: -1;
}

.circle-checkin-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 16px 50px rgba(74, 144, 226, 0.45);
}

.circle-checkin-btn:hover .btn-glow {
  opacity: 0.5;
  filter: blur(12px);
}

.circle-checkin-btn:active {
  transform: scale(0.95);
}

.circle-checkin-btn.btn-pulse {
  animation: btnPulse 2s ease-in-out infinite;
}

@keyframes btnPulse {
  0%,
  100% {
    box-shadow: 0 12px 40px rgba(74, 144, 226, 0.35);
  }
  50% {
    box-shadow: 0 16px 50px rgba(74, 144, 226, 0.5);
  }
}

.circle-checkin-btn.btn-loading {
  cursor: wait;
  animation: none;
}

.circle-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.circle-icon {
  width: 36px;
  height: 36px;
  color: #ffffff;
}

.circle-text {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  font-family: "Noto Sans SC", sans-serif;
  letter-spacing: 4px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.current-time-section {
  margin-bottom: 20px;
}

.time-card {
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.08) 0%,
    rgba(90, 200, 250, 0.08) 100%
  );
  border-radius: 20px;
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(74, 144, 226, 0.1);
}

.current-time {
  font-family: "Noto Serif SC", serif;
  font-size: 36px;
  font-weight: 700;
  color: #4a90e2;
  letter-spacing: 2px;
  line-height: 1.2;
  margin-bottom: 4px;
}

.current-date {
  font-size: 13px;
  color: #8fa1b3;
  letter-spacing: 1px;
}

.location-section {
  margin-bottom: 24px;
}

.location-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(
    135deg,
    rgba(90, 200, 250, 0.1) 0%,
    rgba(74, 144, 226, 0.05) 100%
  );
  border-radius: 16px;
  border: 1px solid rgba(90, 200, 250, 0.2);
}

.location-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #5ac8fa 0%, #4a90e2 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.location-icon svg {
  width: 18px;
  height: 18px;
  color: white;
}

.location-info {
  flex: 1;
  min-width: 0;
}

.location-label {
  font-size: 11px;
  color: #8fa1b3;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.location-coords {
  font-size: 10px;
  color: #a8b8c8;
  letter-spacing: 0;
}

.location-address {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-all;
}

.location-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  color: #4a90e2;
  background: rgba(74, 144, 226, 0.1);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.retry-btn:hover {
  background: rgba(74, 144, 226, 0.2);
  transform: scale(1.02);
}

.retry-btn svg {
  width: 14px;
  height: 14px;
}

.location-loading {
  font-size: 13px;
  color: #8fa1b3;
  line-height: 1.5;
}

.loading-dots::after {
  content: "";
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0% {
    content: "";
  }
  25% {
    content: ".";
  }
  50% {
    content: "..";
  }
  75% {
    content: "...";
  }
  100% {
    content: "";
  }
}

.footer-info {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid rgba(74, 144, 226, 0.1);
}

.footer-info p {
  font-size: 12px;
  color: #8fa1b3;
  letter-spacing: 2px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(44, 62, 80, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}

.success-modal {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 28px;
  padding: 36px 28px;
  width: 100%;
  max-width: 340px;
  text-align: center;
  box-shadow: 0 32px 80px rgba(44, 62, 80, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.success-animation {
  position: relative;
  margin-bottom: 20px;
}

.success-icon-wrapper {
  width: 72px;
  height: 72px;
  margin: 0 auto;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.success-icon {
  width: 36px;
  height: 36px;
  color: #ffffff;
}

.confetti {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.confetti span {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  opacity: 0;
}

.confetti-1 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.1s forwards;
}
.confetti-2 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.15s forwards;
}
.confetti-3 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.2s forwards;
}
.confetti-4 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.25s forwards;
}
.confetti-5 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.3s forwards;
}
.confetti-6 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.35s forwards;
}
.confetti-7 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.4s forwards;
}
.confetti-8 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.45s forwards;
}
.confetti-9 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.5s forwards;
}
.confetti-10 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.55s forwards;
}
.confetti-11 {
  background: #4a90e2;
  animation: confetti 1s ease-out 0.6s forwards;
}
.confetti-12 {
  background: #5ac8fa;
  animation: confetti 1s ease-out 0.65s forwards;
}

.confetti-1 {
  transform: translate(-50%, -50%) rotate(0deg);
}
.confetti-2 {
  transform: translate(-50%, -50%) rotate(30deg);
}
.confetti-3 {
  transform: translate(-50%, -50%) rotate(60deg);
}
.confetti-4 {
  transform: translate(-50%, -50%) rotate(90deg);
}
.confetti-5 {
  transform: translate(-50%, -50%) rotate(120deg);
}
.confetti-6 {
  transform: translate(-50%, -50%) rotate(150deg);
}
.confetti-7 {
  transform: translate(-50%, -50%) rotate(180deg);
}
.confetti-8 {
  transform: translate(-50%, -50%) rotate(210deg);
}
.confetti-9 {
  transform: translate(-50%, -50%) rotate(240deg);
}
.confetti-10 {
  transform: translate(-50%, -50%) rotate(270deg);
}
.confetti-11 {
  transform: translate(-50%, -50%) rotate(300deg);
}
.confetti-12 {
  transform: translate(-50%, -50%) rotate(330deg);
}

@keyframes confetti {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(0);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(
        calc(-50% + var(--x, 60px) * cos(var(--angle, 0deg))),
        calc(-50% + var(--y, 60px) * sin(var(--angle, 0deg)))
      )
      scale(1);
  }
}

.success-title {
  font-family: "Noto Serif SC", serif;
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}

.success-subtitle {
  font-size: 13px;
  color: #8fa1b3;
  margin-bottom: 24px;
}

.success-details {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.06) 0%,
    rgba(90, 200, 250, 0.06) 100%
  );
  border-radius: 14px;
  border: 1px solid rgba(74, 144, 226, 0.1);
  text-align: left;
}

.detail-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-icon svg {
  width: 18px;
  height: 18px;
}

.time-icon {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
}

.time-icon svg {
  color: white;
}

.location-icon-small {
  background: linear-gradient(135deg, #5ac8fa 0%, #4a90e2 100%);
}

.location-icon-small svg {
  color: white;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.detail-label {
  font-size: 11px;
  color: #8fa1b3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 13px;
  font-weight: 500;
  color: #2c3e50;
  word-break: break-all;
}

.confirm-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: "Noto Sans SC", sans-serif;
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.4);
}

.error-modal .error-icon-wrapper {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.error-title {
  color: #4a90e2;
}

.error-message {
  font-size: 13px;
  color: #8fa1b3;
  margin-bottom: 24px;
  padding: 0 12px;
  line-height: 1.6;
}

.error-btn {
  background: linear-gradient(135deg, #4a90e2 0%, #5ac8fa 100%);
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
}

.error-btn:hover {
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.4);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .success-modal,
.modal-leave-active .success-modal {
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .success-modal,
.modal-leave-to .success-modal {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 480px) {
  .checkin-card {
    padding: 32px 24px;
    border-radius: 24px;
  }

  .main-title {
    font-size: 24px;
  }

  .title-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
  }

  .title-icon svg {
    width: 28px;
    height: 28px;
  }

  .circle-checkin-btn {
    width: 130px;
    height: 130px;
  }

  .circle-icon {
    width: 32px;
    height: 32px;
  }

  .circle-text {
    font-size: 16px;
  }

  .current-time {
    font-size: 30px;
  }

  .time-card {
    padding: 16px;
  }

  .location-card {
    padding: 12px;
  }

  .success-modal {
    padding: 28px 24px;
  }
}
</style>
